import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { RootLayout } from "@/components/layout/RootLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PostLoginAuthLink } from "@/components/routing/PostLoginAuthLink";
import { Check, X } from "lucide-react";
import {
  PLAN_COMPARISON_FEATURES,
  PRICING_DISCLAIMER,
  PRICING_PLANS,
} from "@/constants/pricingPlans";

function FeatureValue({ value }: Readonly<{ value: string | boolean }>) {
  if (typeof value === "boolean") {
    return value ? (
      <Check className="h-4 w-4 text-green-600" aria-label="Included" />
    ) : (
      <X className="h-4 w-4 text-muted-foreground" aria-label="Not included" />
    );
  }
  return <span className="text-sm text-muted-foreground">{value}</span>;
}

export default function Pricing() {
  return (
    <>
      <Helmet>
        <title>Pricing | TellerWand - Simple Plans for Indian Investors</title>
        <meta
          name="description"
          content="Compare TellerWand Free and Pro plans. Start free, upgrade to Pro for unlimited tracking, family finance, and advanced analytics."
        />
        <meta property="og:title" content="TellerWand Pricing" />
        <meta
          property="og:description"
          content="Free forever plan plus affordable Pro monthly and annual options for Indian investors and families."
        />
      </Helmet>

      <RootLayout>
        <div className="bg-gradient-to-br from-orange-50 via-white to-orange-100">
          <section className="py-16 px-4">
            <div className="container mx-auto max-w-5xl text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-orange-600 to-orange-500 bg-clip-text text-transparent">
                Simple, Transparent Pricing
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-2">
                Start free. Upgrade when you need unlimited tracking, family finance, and deeper
                insights.
              </p>
              <p className="text-sm text-gray-500">{PRICING_DISCLAIMER}</p>
            </div>
          </section>

          <section className="pb-8 px-4">
            <div className="container mx-auto max-w-5xl grid gap-6 md:grid-cols-3">
              <Card className="border-2 border-orange-100">
                <CardHeader>
                  <CardTitle>{PRICING_PLANS.free.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-3xl font-bold">{PRICING_PLANS.free.priceLabel}</p>
                    <p className="text-sm text-muted-foreground">
                      {PRICING_PLANS.free.periodLabel}
                    </p>
                  </div>
                  <p className="text-sm text-gray-600">{PRICING_PLANS.free.description}</p>
                  <Button className="w-full" asChild>
                    <PostLoginAuthLink mode="signup">{PRICING_PLANS.free.cta}</PostLoginAuthLink>
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-2 border-orange-300 shadow-md">
                <CardHeader>
                  <CardTitle>{PRICING_PLANS.monthly.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-3xl font-bold">{PRICING_PLANS.monthly.priceLabel}</p>
                    <p className="text-sm text-muted-foreground">
                      {PRICING_PLANS.monthly.periodLabel}
                    </p>
                  </div>
                  <p className="text-sm text-gray-600">{PRICING_PLANS.monthly.description}</p>
                  <Button className="w-full bg-gradient-to-r from-orange-500 to-orange-600" asChild>
                    <PostLoginAuthLink mode="signup">{PRICING_PLANS.monthly.cta}</PostLoginAuthLink>
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-2 border-orange-400 shadow-lg relative">
                <CardHeader>
                  <CardTitle>{PRICING_PLANS.annual.name}</CardTitle>
                  <p className="text-xs font-medium text-orange-700">
                    {PRICING_PLANS.annual.savingsLabel}
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-3xl font-bold">{PRICING_PLANS.annual.priceLabel}</p>
                    <p className="text-sm text-muted-foreground">
                      {PRICING_PLANS.annual.periodLabel} · {PRICING_PLANS.annual.equivalentLabel}
                    </p>
                  </div>
                  <p className="text-sm text-gray-600">{PRICING_PLANS.annual.description}</p>
                  <Button className="w-full bg-gradient-to-r from-orange-500 to-orange-600" asChild>
                    <PostLoginAuthLink mode="signup">{PRICING_PLANS.annual.cta}</PostLoginAuthLink>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </section>

          <section className="py-12 px-4 bg-white">
            <div className="container mx-auto max-w-4xl">
              <h2 className="text-2xl font-bold text-center mb-8">Compare plans</h2>
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      <th className="text-left p-4 font-semibold">Feature</th>
                      <th className="text-center p-4 font-semibold">Free</th>
                      <th className="text-center p-4 font-semibold">Pro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PLAN_COMPARISON_FEATURES.map((row) => (
                      <tr key={row.name} className="border-b last:border-b-0">
                        <td className="p-4 font-medium">{row.name}</td>
                        <td className="p-4 text-center">
                          <div className="flex justify-center">
                            <FeatureValue value={row.free} />
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex justify-center">
                            <FeatureValue value={row.pro} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-center text-sm text-muted-foreground mt-6">
                Already have an account?{" "}
                <PostLoginAuthLink className="text-orange-600 hover:underline font-medium">
                  Sign in
                </PostLoginAuthLink>{" "}
                to manage your subscription, or read our{" "}
                <Link to="/refunds" className="text-orange-600 hover:underline">
                  Refunds & Cancellations Policy
                </Link>
                .
              </p>
            </div>
          </section>
        </div>
      </RootLayout>
    </>
  );
}
